import { useEffect, useState } from 'react'
import { api } from '../api'
import { type Lang, useI18n } from '../i18n'
import { navigate } from '../router'
import { LangSwitch } from '../ui/LangSwitch'

export type LegalDoc = 'terms' | 'privacy'

/** A paragraph, or a list when an array. */
type Block = string | string[]

interface Section {
  title: string
  body: Block[]
}

interface Doc {
  title: string
  intro: string
  sections: Section[]
  /** The last section: how to reach the owner. */
  contact: { title: string; with: string; without: string }
}

const UPDATED = '2026-10-15'

const docs: Record<LegalDoc, Record<Lang, Doc>> = {
  terms: {
    ru: {
      title: 'Условия использования',
      intro:
        'Эти условия действуют для сайта chillio.space и активности chillio в Discord (дальше — «сервис»). Пользуясь сервисом, вы соглашаетесь с ними.',
      sections: [
        {
          title: 'Что такое chillio',
          body: [
            'chillio — небольшой частный сервис для совместного просмотра: комнаты, синхронное воспроизведение, чат и активность в Discord. Сервис бесплатный и предоставляется «как есть».',
          ],
        },
        {
          title: 'Доступ',
          body: [
            'Комнаты на сайте создаются по токену, который выдаёт владелец сервиса. В Discord комнаты создаются на серверах, подключённых этим токеном. Остальные участники заходят по приглашению или по ссылке на комнату.',
            'Используя chillio в Discord, вы также соблюдаете Условия использования и Правила сообщества Discord. Пользоваться сервисом можно, если вы достигли минимального возраста, с которого в вашей стране разрешено пользоваться Discord.',
          ],
        },
        {
          title: 'Фильмы и другой контент',
          body: [
            'chillio не размещает и не подбирает фильмы. Источник (magnet-ссылку, торрент-файл или раздачу из каталога) добавляет владелец комнаты; сервер загружает его по BitTorrent и показывает участникам этой комнаты.',
            'Добавляя источник, вы подтверждаете, что имеете право его смотреть и показывать участникам комнаты, и отвечаете за свой выбор. Нельзя добавлять материалы, которые нарушают закон или чужие права, в том числе авторские.',
          ],
        },
        {
          title: 'Поведение',
          body: [
            'В чате и комнатах запрещены:',
            ['спам и реклама;', 'оскорбления, травля и угрозы;', 'незаконный контент и ссылки на него;', 'попытки нарушить работу сервиса или получить чужой доступ.'],
            'Владелец комнаты может удалять участников из неё. Владелец сервиса может удалить комнату или закрыть доступ к сервису при нарушении этих условий.',
          ],
        },
        {
          title: 'Правообладателям',
          body: [
            'Если в комнате показывают материал, права на который принадлежат вам, напишите нам (контакты ниже), указав ссылку на комнату или название раздачи. Мы удалим источник и комнату.',
          ],
        },
        {
          title: 'Сторонние сервисы',
          body: [
            'Описания фильмов и постеры в каталоге взяты из TMDb. This product uses the TMDB API but is not endorsed or certified by TMDB.',
            'chillio не связан с Discord, TMDb и торрент-трекерами и не одобрен ими.',
          ],
        },
        {
          title: 'Без гарантий',
          body: [
            'Сервис может работать с перерывами, меняться или прекратить работу без предупреждения. В пределах, допустимых законом, владелец сервиса не отвечает за убытки, связанные с его использованием или недоступностью.',
          ],
        },
        {
          title: 'Изменения',
          body: ['Условия могут обновляться; дата последней редакции указана вверху. Продолжая пользоваться сервисом, вы принимаете новую редакцию.'],
        },
      ],
      contact: { title: 'Контакты', with: 'Вопросы и жалобы:', without: 'Вопросы и жалобы можно передать владельцу сервиса через Discord-сервер, где вы пользуетесь chillio.' },
    },
    en: {
      title: 'Terms of Service',
      intro:
        'These terms apply to the chillio.space website and the chillio activity in Discord (the "service"). By using the service you agree to them.',
      sections: [
        {
          title: 'What chillio is',
          body: [
            'chillio is a small private service for watching together: rooms, synchronized playback, chat and a Discord activity. It is free and provided "as is".',
          ],
        },
        {
          title: 'Access',
          body: [
            'Rooms on the website are created with a token issued by the owner of the service. In Discord, rooms are created in servers connected with that token. Everyone else joins by invitation or by a room link.',
            'When using chillio in Discord you also follow the Discord Terms of Service and Community Guidelines. You may use the service if you are at least the minimum age required to use Discord in your country.',
          ],
        },
        {
          title: 'Films and other content',
          body: [
            'chillio does not host or pick films. A source (a magnet link, a torrent file or a release from the catalog) is added by the room owner; the server downloads it over BitTorrent and shows it to the members of that room.',
            'By adding a source you confirm that you have the right to watch it and show it to the room, and you are responsible for your choice. Do not add material that breaks the law or the rights of others, including copyright.',
          ],
        },
        {
          title: 'Conduct',
          body: [
            'In chat and rooms the following is not allowed:',
            ['spam and advertising;', 'insults, harassment and threats;', 'illegal content and links to it;', 'attempts to disrupt the service or gain access that is not yours.'],
            'Room owners can remove people from their rooms. The owner of the service can delete a room or close access to the service when these terms are broken.',
          ],
        },
        {
          title: 'Rights holders',
          body: [
            'If a room shows material you hold the rights to, write to us (see below) with the room link or the release name. We will remove the source and the room.',
          ],
        },
        {
          title: 'Third-party services',
          body: [
            'Film descriptions and posters in the catalog come from TMDB. This product uses the TMDB API but is not endorsed or certified by TMDB.',
            'chillio is not affiliated with or endorsed by Discord, TMDB or torrent trackers.',
          ],
        },
        {
          title: 'No warranty',
          body: [
            'The service may be interrupted, changed or discontinued without notice. To the extent permitted by law, the owner of the service is not liable for losses related to its use or unavailability.',
          ],
        },
        {
          title: 'Changes',
          body: ['These terms may be updated; the date of the latest version is shown at the top. By continuing to use the service you accept the new version.'],
        },
      ],
      contact: { title: 'Contact', with: 'Questions and complaints:', without: 'Questions and complaints can be passed to the owner of the service through the Discord server where you use chillio.' },
    },
  },
  privacy: {
    ru: {
      title: 'Политика конфиденциальности',
      intro: 'Здесь описано, какие данные собирает chillio (сайт chillio.space и активность в Discord), зачем и как долго их хранит.',
      sections: [
        {
          title: 'Что мы храним',
          body: [
            'На сайте:',
            ['случайный идентификатор пользователя и сессию в cookie (в базе хранится только хэш сессии);', 'имя, которое вы ввели при входе в комнату.'],
            'В Discord — при запуске активности и с вашего разрешения (доступы identify, guilds, guilds.members.read, rpc.activities.write):',
            [
              'ваш ID в Discord, имя пользователя и отображаемое имя;',
              'ник и аватар на сервере Discord;',
              'ID сервера и канала, где запущена активность, и название канала (им называется комната).',
            ],
            'Токен доступа Discord нужен только при входе и не сохраняется. Мы не читаем ваши сообщения, список друзей и почту.',
            'В комнатах: название, участники и их роли, добавленные источники (торренты), позиция просмотра и сообщения чата.',
            'Настройки (язык, громкость, вид субтитров, имя) хранятся только в вашем браузере (localStorage).',
          ],
        },
        {
          title: 'Зачем',
          body: [
            [
              'чтобы работали комнаты, синхронное воспроизведение и чат;',
              'чтобы показывать участников с их именами и аватарками;',
              'чтобы показывать в вашем профиле Discord, что вы смотрите (статус «Смотрит …» видят те, кому Discord его показывает);',
              'чтобы защищаться от подбора токена: счётчик попыток хранится в памяти сервера и сбрасывается через 10 минут.',
            ],
          ],
        },
        {
          title: 'Чего мы не делаем',
          body: [
            'Нет рекламы, аналитики и сторонних трекеров. Мы не продаём и не передаём ваши данные. IP-адреса не записываются в журналы.',
          ],
        },
        {
          title: 'Сторонние сервисы',
          body: [
            [
              'Discord — вход в активность. Аватарки загружаются через наш сервер.',
              'TMDb — описания и постеры фильмов. Постеры загружаются через наш сервер, поэтому TMDb не видит ваш адрес.',
              'Торрент-трекеры и участники BitTorrent — с ними соединяется наш сервер, а не ваш браузер.',
            ],
            'Шрифты и все файлы сайта отдаются с chillio.space.',
          ],
        },
        {
          title: 'Cookies',
          body: [
            'Только необходимые для работы:',
            [
              'chillio_session — ваша сессия (до 400 дней на сайте, 30 дней в Discord);',
              'chillio_linked — вход на сайт из Discord кнопкой «В браузере» (30 дней).',
            ],
          ],
        },
        {
          title: 'Как долго',
          body: [
            [
              'комнаты Discord вместе с чатом удаляются через 24 часа без активности;',
              'комнаты сайта и их чат — когда владелец удалит комнату;',
              'профиль (ID, имя, аватар) — пока вы не попросите его удалить.',
            ],
          ],
        },
        {
          title: 'Ваши права',
          body: [
            'Вы можете запросить копию своих данных или их удаление (контакты ниже). Доступ chillio к Discord можно отозвать в Discord: Настройки → Авторизованные приложения.',
          ],
        },
        {
          title: 'Изменения',
          body: ['Политика может обновляться; дата последней редакции указана вверху.'],
        },
      ],
      contact: { title: 'Контакты', with: 'Запросы о данных:', without: 'Запросы о данных можно передать владельцу сервиса через Discord-сервер, где вы пользуетесь chillio.' },
    },
    en: {
      title: 'Privacy Policy',
      intro: 'This page explains what data chillio (the chillio.space website and the Discord activity) collects, why, and for how long.',
      sections: [
        {
          title: 'What we store',
          body: [
            'On the website:',
            ['a random user ID and a session in a cookie (the database keeps only a hash of the session);', 'the name you entered when joining a room.'],
            'In Discord — when you launch the activity and with your permission (scopes identify, guilds, guilds.members.read, rpc.activities.write):',
            ['your Discord ID, username and display name;', 'your nickname and avatar in the Discord server;', 'the IDs of the server and channel where the activity runs, and the channel name (it names the room).'],
            'The Discord access token is used only to sign in and is not stored. We do not read your messages, friends list or email.',
            'In rooms: the name, members and their roles, added sources (torrents), the playback position and chat messages.',
            'Settings (language, volume, subtitle style, name) stay in your browser only (localStorage).',
          ],
        },
        {
          title: 'Why',
          body: [
            [
              'to run rooms, synchronized playback and chat;',
              'to show members with their names and avatars;',
              'to show what you are watching in your Discord profile (the "Watching …" status is visible to whoever Discord shows it to);',
              'to protect against token guessing: attempts are counted in server memory and reset after 10 minutes.',
            ],
          ],
        },
        {
          title: 'What we do not do',
          body: ['No ads, analytics or third-party trackers. We do not sell or share your data. IP addresses are not written to logs.'],
        },
        {
          title: 'Third-party services',
          body: [
            [
              'Discord — signing in to the activity. Avatars are loaded through our server.',
              'TMDB — film descriptions and posters. Posters are loaded through our server, so TMDB does not see your address.',
              'Torrent trackers and BitTorrent peers — our server connects to them, not your browser.',
            ],
            'Fonts and all files of the site are served from chillio.space.',
          ],
        },
        {
          title: 'Cookies',
          body: [
            'Only the ones needed for the service to work:',
            ['chillio_session — your session (up to 400 days on the website, 30 days in Discord);', 'chillio_linked — signing in on the website from Discord with "In browser" (30 days).'],
          ],
        },
        {
          title: 'How long',
          body: [
            [
              'Discord rooms and their chat are deleted after 24 hours without activity;',
              'website rooms and their chat — when the owner deletes the room;',
              'your profile (ID, name, avatar) — until you ask us to delete it.',
            ],
          ],
        },
        {
          title: 'Your rights',
          body: [
            'You can ask for a copy of your data or for its deletion (see below). You can revoke chillio’s access in Discord: Settings → Authorized Apps.',
          ],
        },
        {
          title: 'Changes',
          body: ['This policy may be updated; the date of the latest version is shown at the top.'],
        },
      ],
      contact: { title: 'Contact', with: 'Data requests:', without: 'Data requests can be passed to the owner of the service through the Discord server where you use chillio.' },
    },
  },
}

const labels: Record<Lang, { updated: string; home: string; other: Record<LegalDoc, string> }> = {
  ru: { updated: 'Редакция от', home: 'На главную', other: { terms: 'Условия использования', privacy: 'Политика конфиденциальности' } },
  en: { updated: 'Last updated', home: 'Home', other: { terms: 'Terms of Service', privacy: 'Privacy Policy' } },
}

/** The terms of service or the privacy policy (/terms, /privacy). */
export function LegalPage({ doc }: { doc: LegalDoc }) {
  const { lang } = useI18n()
  const [contact, setContact] = useState<string | undefined>()
  const d = docs[doc][lang]
  const l = labels[lang]
  const other: LegalDoc = doc === 'terms' ? 'privacy' : 'terms'

  useEffect(() => {
    api
      .features()
      .then((f) => setContact(f.contact))
      .catch(() => {})
  }, [])
  useEffect(() => {
    document.title = `${d.title} · chillio`
    return () => {
      document.title = 'chillio'
    }
  }, [d.title])

  const go = (to: string) => (e: React.MouseEvent) => {
    e.preventDefault()
    navigate(to)
    window.scrollTo(0, 0)
  }

  return (
    <main className="legal">
      <header className="legal__top">
        <a href="/" className="logo" onClick={go('/')} aria-label={l.home}>
          chillio
        </a>
        <LangSwitch />
      </header>
      <article className="legal__doc">
        <h1>{d.title}</h1>
        <p className="legal__updated muted">
          {l.updated} {new Date(UPDATED).toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
        <p>{d.intro}</p>
        {d.sections.map((s) => (
          <section key={s.title}>
            <h2>{s.title}</h2>
            {s.body.map((b, i) =>
              Array.isArray(b) ? (
                <ul key={i}>
                  {b.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p key={i}>{b}</p>
              ),
            )}
          </section>
        ))}
        <section>
          <h2>{d.contact.title}</h2>
          {contact ? (
            <p>
              {d.contact.with} <a href={contactHref(contact)}>{contact}</a>
            </p>
          ) : (
            <p>{d.contact.without}</p>
          )}
        </section>
      </article>
      <footer className="legal__footer">
        <a href={`/${other}`} onClick={go(`/${other}`)}>
          {l.other[other]}
        </a>
      </footer>
    </main>
  )
}

/** An address as a link: email addresses become mailto:. */
function contactHref(contact: string) {
  return /^https?:\/\//.test(contact) ? contact : `mailto:${contact}`
}

/** Links to both documents, for page footers. */
export function LegalLinks() {
  const { lang } = useI18n()
  const l = labels[lang]
  return (
    <nav className="legal-links" aria-label="legal">
      {(['terms', 'privacy'] as const).map((d) => (
        <a
          key={d}
          href={`/${d}`}
          onClick={(e) => {
            e.preventDefault()
            navigate(`/${d}`)
            window.scrollTo(0, 0)
          }}
        >
          {l.other[d]}
        </a>
      ))}
    </nav>
  )
}
